<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="container" @click.self="!loading">
    <div v-loading.center="{ isLoading: !!loading }" class="wrapper">
      <div v-if="modal" class="text-center">
        <h3 class="mb-2 title">
          {{ modal.title }}
        </h3>

        <p v-if="modal.message" class="mb-2 subtitle">
          <span v-html="modal.message.text" />
        </p>
      </div>

      <div class="button-container">
        <div v-for="btn in modal.buttons" :key="btn.id" class="btn-wrapper">
          <ThemeButton
            class="block-button"
            :outlined="btn.type === 'outlined'"
            :text="btn.type === 'text'"
            @click="btn.click"
          >
            {{ btn.text }}
          </ThemeButton>
          <span v-if="btn.description" class="btn-description">{{ btn.description }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import loading from '@/components/loading';
import ThemeButton from '@/components/ThemeButton.vue';
import * as Locale from '@/plugins/i18n';
import { TranslateResult } from 'vue-i18n';
import LogPageEventMixin, { PAGE_TRACKING, PAGE_EVENTS } from '@/mixins/logPageEvent';
import { analytics, BTN_EVTS } from '@/common/logs';

enum Route {
  LIVE_CHAT_OVERLOADED = 'LIVE_CHAT_OVERLOADED',
  VOICE_CHAT_OVERLOADED = 'VOICE_CHAT_OVERLOADED',
  BOTH_LIVE_VOICE_CHAT_OVERLOADED = 'BOTH_LIVE_VOICE_CHAT_OVERLOADED',
}

const btn = (
  type: 'outlined' | 'primary' | 'text',
  text: string | TranslateResult,
  click: (e: MouseEvent) => void,
  description?: string | TranslateResult
) => ({ id: `${type}-${text}`, text, type, description, click });

const OVERLOADED_CHANNEL_MODAL_EVENTS = {
  back: 'back',
  openRequest: 'openRequest',
  liveChat: 'liveChat',
  call: 'call',
} as const;

type ThresholdLimitModalEvent = (typeof OVERLOADED_CHANNEL_MODAL_EVENTS)[keyof typeof OVERLOADED_CHANNEL_MODAL_EVENTS];

const MODAL_EVENTS_TO_CLICK_TRACK_EVENTS_MAP = {
  [OVERLOADED_CHANNEL_MODAL_EVENTS.back]: BTN_EVTS.MODAL_THRESHOLD_LIMIT_BACK,
  [OVERLOADED_CHANNEL_MODAL_EVENTS.openRequest]: BTN_EVTS.MODAL_THRESHOLD_LIMIT_OPEN_REQUEST,
  [OVERLOADED_CHANNEL_MODAL_EVENTS.liveChat]: BTN_EVTS.MODAL_THRESHOLD_LIMIT_LIVE_CHAT,
  [OVERLOADED_CHANNEL_MODAL_EVENTS.call]: BTN_EVTS.MODAL_THRESHOLD_LIMIT_CALL,
} as const;

@Component({
  directives: { loading },
  components: { ThemeButton },
  mixins: [LogPageEventMixin],
})
export default class ThresholdLimitModal extends Vue {
  @Prop() readonly liveChatWaitTimeDescription?: string | TranslateResult;
  @Prop() readonly callWaitTimeDescription?: string | TranslateResult;
  @Prop() readonly hasLiveChatOnlineAgents?: boolean;
  @Prop() readonly hasCallOnlineAgents?: boolean;
  /** Channel the user tried to use when the threshold limit was hit (drives which message to show). */
  @Prop() readonly requestedChannel?: 'liveChat' | 'call' | null;

  loading = false;
  route: Route = Route.LIVE_CHAT_OVERLOADED;

  created() {
    if (!this.hasLiveChatOnlineAgents && !this.hasCallOnlineAgents) {
      this.route = Route.BOTH_LIVE_VOICE_CHAT_OVERLOADED;
    } else if (this.requestedChannel === 'call') {
      this.route = Route.VOICE_CHAT_OVERLOADED;
    } else {
      this.route = Route.LIVE_CHAT_OVERLOADED;
    }
  }

  get [PAGE_TRACKING]() {
    return {
      name: PAGE_EVENTS.MODAL_THRESHOLD_LIMIT,
      metadata: {
        hasLiveChatOnlineAgents: this.hasLiveChatOnlineAgents,
        hasCallOnlineAgents: this.hasCallOnlineAgents,
      },
    };
  }

  get modal() {
    const createEventHandler = (event: ThresholdLimitModalEvent) => () => {
      const modalEvent = MODAL_EVENTS_TO_CLICK_TRACK_EVENTS_MAP[event];

      if (modalEvent) {
        analytics.trackClick({
          name: modalEvent,
        });
      }

      this.$emit(event);
    };

    const openRequestBtn = btn(
      'primary',
      Locale.i18n.t('page.connect.open_request.text'),
      createEventHandler('openRequest')
    );

    const backButton = btn('text', Locale.i18n.t('page.connect.overloaded.cancel'), createEventHandler('back'));

    const hasCallOnlineAgents = this.hasCallOnlineAgents;
    const hasLiveChatOnlineAgents = this.hasLiveChatOnlineAgents;
    const callWaitTimeDescription = this.callWaitTimeDescription;

    const RouteModal = {
      [Route.LIVE_CHAT_OVERLOADED]: () => ({
        title: Locale.i18n.t('page.connect.overloaded.live_chat_modal.title'),
        message: {
          text: (() => {
            const res = [Locale.i18n.t('page.connect.overloaded.common.representatives_are_busy')];

            if (hasCallOnlineAgents) {
              res.push(Locale.i18n.t('page.connect.overloaded.live_chat_modal.open_request_or_call'));
            } else {
              res.push(Locale.i18n.t('page.connect.overloaded.common.open_request'));
            }

            return res.join(' ');
          })(),
        },
        buttons: (() => {
          if (hasCallOnlineAgents) {
            return [
              openRequestBtn,
              btn('primary', Locale.i18n.t('page.connect.call'), createEventHandler('call'), callWaitTimeDescription),
              backButton,
            ];
          }

          return [openRequestBtn, backButton];
        })(),
      }),
      [Route.VOICE_CHAT_OVERLOADED]: () => ({
        title: Locale.i18n.t('page.connect.overloaded.voice_chat_modal.title'),
        message: {
          text: (() => {
            const res = [Locale.i18n.t('page.connect.overloaded.common.representatives_are_busy')];

            if (hasLiveChatOnlineAgents) {
              res.push(Locale.i18n.t('page.connect.overloaded.voice_chat_modal.open_request_or_start_live_chat'));
            } else {
              res.push(Locale.i18n.t('page.connect.overloaded.common.open_request'));
            }

            return res.join(' ');
          })(),
        },
        buttons: (() => {
          if (hasLiveChatOnlineAgents) {
            return [
              openRequestBtn,
              btn(
                'primary',
                Locale.i18n.t('page.connect.live_chat'),
                createEventHandler('liveChat'),
                this.liveChatWaitTimeDescription
              ),
              backButton,
            ];
          }

          return [openRequestBtn, backButton];
        })(),
      }),
      [Route.BOTH_LIVE_VOICE_CHAT_OVERLOADED]: () => ({
        title: Locale.i18n.t('page.connect.overloaded.both_live_voice_chat_modal.title'),
        message: {
          html: true,
          text: `${Locale.i18n.t(
            'page.connect.overloaded.both_live_voice_chat_modal.message'
          )}</br></br>${Locale.i18n.t('page.connect.overloaded.both_live_voice_chat_modal.bottom_message')}`,
        },
        buttons: [openRequestBtn, backButton],
      }),
    };

    return RouteModal[this.route]?.();
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/base.scss';
$border-radius: 5px;

.container {
  height: 100vh;
  width: 100vw;
  background: rgba(0, 0, 0, 0.23);
  display: flex;
  justify-content: center;
  align-items: center;
}

.wrapper {
  max-width: 75vw;
  font-size: 1.5rem;
  border-radius: $border-radius;
  position: relative;
  background: $continue-chat-background-color;
  padding: 3rem;
  color: $continue-chat-text-color;

  box-shadow:
    0 10px 20px rgba(0, 0, 0, 0.19),
    0 6px 6px rgba(0, 0, 0, 0.23);

  .btn-wrapper {
    display: flex;
    flex-direction: column;

    text-align: center;
  }

  .block-button {
    padding: 1.1rem 0;
    margin-top: 10px;
    cursor: pointer;
  }

  .btn-description {
    margin-top: 4px;
    font-size: $size-xs;
    color: $action-sheet-text-color;
  }

  .button-container {
    display: flex;
    flex-direction: column;
  }

  .title {
    position: relative;
    padding: 1rem;
    z-index: 101;
    display: flex;
    align-items: center;
    font-weight: 500;
    color: $dark;
    border: {
      top-left-radius: $border-radius;
      top-right-radius: $border-radius;
    }
  }
}
</style>
